import React, {Fragment, useEffect, useRef, useState} from 'react'
import classNames from "classnames";
import { Menu, Transition } from '@headlessui/react'
import { Link } from "react-router-dom";
import { DotsVerticalIcon, DotsHorizontalIcon } from "@heroicons/react/solid";

import { createPopper } from "@popperjs/core/lib/popper-lite";
import flip from "@popperjs/core/lib/modifiers/flip";
import preventOverflow from "@popperjs/core/lib/modifiers/preventOverflow";

const classes = {
    container: "relative inline-block text-left",
    items: (origin) => classNames(
        "z-10 w-56 my-2 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none",
        {
            "origin-top-left": origin === 'tl',
            "origin-top-right": origin === 'tr',
            "origin-bottom-left": origin === 'bl',
            "origin-bottom-right": origin === 'br',
        }
    )
};

export default function Dropdown({button, origin, children}) {

    const [popper, setPopper] = useState(null);

    const triggerRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {

        if (! triggerRef.current) {
            return;
        }

        if (! popupRef.current) {
            return;
        }

        const newPopper = createPopper(
            triggerRef.current,
            popupRef.current,
            {
                placement: 'bottom-start',
                strategy: 'fixed',
                modifiers: [
                    flip,
                    preventOverflow,
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 10]
                        }
                    }
                ]
            }
        );

        setPopper(newPopper);

    }, [triggerRef, popupRef, setPopper]);

    function renderButton(triggerRef)
    {
        switch(typeof button) {
            case 'function':
                return button({triggerRef});
            default:
                return <Dropdown.Button ref={triggerRef}>{button}</Dropdown.Button>;
        }
    }

    return (
        <>
        <Menu>
            {({ open }) => <>
                {
                    renderButton(triggerRef)
                }
                <div className={classes.container}>
                    <span ref={popupRef} className='z-30'>
                        <Transition
                            show={open}
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"

                            beforeEnter={() => popper ? setTimeout(popper.forceUpdate) : null}
                        >
                            <Menu.Items static className={classes.items(origin)}>
                                <div className="py-1">
                                    {children}
                                </div>
                            </Menu.Items>
                        </Transition>
                    </span>
                </div>
            </>
            }
        </Menu>
        </>
    )
}

Dropdown.Item = ({className, children, onClick, danger = false, disabled = false, ...props}) => {

    const classes = (active, disabled) => classNames(
        className,
        'flex items-center space-x-2 w-full text-left px-4 py-2 text-sm',
        {
            'text-gray-700': !danger && ! active,
            'bg-gray-100 text-gray-900': !danger && active,
        },
        {
            'text-red-600': danger && ! active,
            'text-red-50 bg-red-600': danger && active,
        },
        {
            'cursor-not-allowed': disabled
        }
    );

    return <Menu.Item disabled={disabled}>
        {({active, disabled}) => (
            <button onClick={onClick} className={classes(active, disabled)} {...props}>
                {children}
            </button>
        )}
    </Menu.Item>
};

Dropdown.LinkItem = ({className, children, disabled = false, ...props}) => {

    const classes = (active, disabled) => classNames(
        className,
        'flex items-center space-x-2 px-4 py-2 text-sm',
        {
            'bg-gray-100 text-gray-900': active,
            'text-gray-700': ! active
        },
        {
            'cursor-not-allowed': disabled
        }
    );

    return <Menu.Item disabled={disabled}>
        {({active, disabled}) => (
            <Link {...props} className={classes(active, disabled)}>
                {children}
            </Link>
        )}
    </Menu.Item>
};

Dropdown.AnchorItem = ({className, children, href, disabled = false, ...props}) => {

    const classes = (active, disabled) => classNames(
        className,
        'flex items-center space-x-2 px-4 py-2 text-sm',
        {
            'bg-gray-100 text-gray-900': active,
            'text-gray-700': ! active
        },
        {
            'cursor-not-allowed': disabled
        }
    );

    return <Menu.Item disabled={disabled}>
        {({active, disabled}) => (
            <a {...props} href={href} className={classes(active, disabled)}>
                {children}
            </a>
        )}
    </Menu.Item>
};

Dropdown.Button = Menu.Button;

// Dropdown.Button = ({children}) => {
//     return <Menu.Button className="rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
//         <span className="sr-only">Open options</span>
//         {children}
//     </Menu.Button>;
// };

Dropdown.VerticalDotsButton = ({triggerRef}) => {
    const classes = open => classNames(
        "w-6 h-6 inline-flex items-center justify-center rounded-full",
        "transition ease-in-out duration-150",
        "hover:text-gray-500 hover:bg-gray-100",
        "outline-none",
        {
            "text-gray-400": ! open,
            "text-gray-500 bg-gray-100 outline-none text-gray-500 bg-gray-100": open
        }
    );

    return <Dropdown.Button ref={triggerRef} className='focus:outline-none'>
        {({open}) => (
            <div className={classes(open)}>
                <span className="sr-only">Open options</span>
                <DotsVerticalIcon className='w-4' aria-hidden="true"/>
            </div>
        )}
    </Dropdown.Button>;
};

Dropdown.Divider = () => <div className="border-t border-gray-100 my-1" />;